import React, { useMemo, useEffect, useRef } from "react";
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  LineSeries,
  ColumnSeries,
  Category,
  Logarithmic,
  Tooltip,
  Inject,
  Legend,
} from "@syncfusion/ej2-react-charts";
import { formatNumber } from "../../utils/formatNumber";
import "./Chart.css";

const DEFAULT_PALETTE = ["#eb4132", "#fcbd01", "#31aa52", "#1976d2"];

const getColor = (i) => DEFAULT_PALETTE[i % DEFAULT_PALETTE.length];

export default function MultiLineAvgChart({
  title = "Average Active TV",
  series = [],
  refreshKey,
  yAxisScale = "linear",
  yName = "active",
  logBase = 10,
  mode = "line", // 🔥 NEW
  startTime = "00:00",  // ✅ ekle
  endTime = "23:59",    // ✅ ekle
}) {
  const chartRef = useRef(null);

  useEffect(() => {
    chartRef.current?.refresh();
  }, [refreshKey]);

  const safeSeries = useMemo(
    () => (Array.isArray(series) ? series : []),
    [series]
  );

  const useLog = yAxisScale === "log";

  // =======================
  // LINE DATA
  // =======================
  const plottedSeries = useMemo(() => {
    return safeSeries.map((s) => ({
      ...s,
      data: (s.data || []).map((p) => {
        const raw = Number(p?.active);
        const safe = Number.isFinite(raw) ? Math.max(0, raw) : 0;

        return useLog
          ? { ...p, rawActive: safe, activePlot: safe <= 0 ? 1 : safe }
          : { ...p, rawActive: safe, active: safe };
      }),
    }));
  }, [safeSeries, useLog]);

  // =======================
  // BAR DATA (TV8)
  // =======================
  const avgData = useMemo(() => {
    const [startH, startM] = (startTime || "00:00").split(":").map(Number);
    const [endH, endM] = (endTime || "23:59").split(":").map(Number);
    const minCount = (endH * 60 + endM) - (startH * 60 + startM); // ✅ eski kodla aynı

    return safeSeries
      .map((s, i) => {
        const total = s.data.reduce((sum, d) => sum + (Number(d.active) || 0), 0);
        const avg = minCount > 0 ? total / minCount : 0;

        return {
          name: s.name,
          value: Math.round(avg),
          colorIndex: i,
        };
      })
      .reverse();
  }, [safeSeries, startTime, endTime]);

  // =======================
  // TOOLTIP
  // =======================
  const handleTooltipRender = (args) => {
    const raw = args?.point?.rawActive;
    if (raw !== undefined) {
      args.text = args.text.replace(/:\s*.*$/, `: ${formatNumber(raw)}`);
    }
  };

  // =======================
  // AXIS
  // =======================
  const handleAxisLabelRender = (args) => {
    if (args.axis?.name !== "primaryYAxis") return;
    const val = Number(args.value);

    if (!isNaN(val)) {
      if (useLog) {
        const realValue = Math.pow(logBase, val);
        args.text = formatNumber(realValue);
      } else {
        args.text = formatNumber(val);
      }
    }
  };

  return (
    <div className="chart-container">
      <h3 className="chart-title">{title}</h3>

      {/* =======================
          LINE CHART
      ======================= */}
      <ChartComponent
        ref={chartRef}
        primaryXAxis={{
          valueType: "Category",
          labelIntersectAction: "Rotate45",
        }}
        primaryYAxis={{
          minimum: useLog ? 1 : 0,
          valueType: useLog ? "Logarithmic" : "Double",
          logBase: useLog ? logBase : undefined,
        }}
        legendSettings={{
          visible: true,
          position: "Top",
        }}
        tooltip={{ enable: true }}
        tooltipRender={handleTooltipRender}
        axisLabelRender={handleAxisLabelRender}
        height="420px"
      >
        <Inject
          services={[LineSeries, Category, Logarithmic, Tooltip, Legend]}
        />

        <SeriesCollectionDirective>
          {plottedSeries.map((s, i) => {
            const color = getColor(i);

            return (
              <SeriesDirective
                key={i}
                dataSource={s.data}
                xName="x"
                yName={useLog ? "activePlot" : yName}
                type="Line"
                name={s.name}
                width={3}
                fill={color}
                columnSpacing={2}
              />
            );
          })}
        </SeriesCollectionDirective>
      </ChartComponent>

      {/* =======================
          BAR CHART (TV8 ONLY)
      ======================= */}
      {mode === "tv8" && (
        <div style={{ marginTop: 30 }}>
          <h4>Average of Selected Time</h4>

          <ChartComponent
            primaryXAxis={{ valueType: "Category" }}
            tooltip={{ enable: true }}
            legendSettings={{ visible: true, position: "Right" }}
            height="300px"
            columnSpacing={0.3}
          >
            <Inject services={[ColumnSeries, Category, Tooltip, Legend]} />

            <SeriesCollectionDirective>
              {avgData.map((d, i) => (
                <SeriesDirective
                  key={i}
                  dataSource={[{ x: "Average TV", y: d.value }]}
                  xName="x"
                  yName="y"
                  type="Column"
                  name={d.name}
                  fill={getColor(d.colorIndex)}
                  columnSpacing={0.1}
                />
              ))}
            </SeriesCollectionDirective>
          </ChartComponent>
        </div>
      )}
    </div>
  );
}